/**
 * AgentComposer — Center search hub with inline config pills in the bottom rail.
 */

import { ArrowUp, Square } from 'lucide-react';
import React, { useEffect, useImperativeHandle, useRef, forwardRef, useState } from 'react';

import { useTypewriterPlaceholder } from '../hooks/useTypewriterPlaceholder';
import { StreamCaret } from './StreamCaret';

export interface AgentComposerHandle {
  setValue: (text: string) => void;
  focus: () => void;
}

export interface AgentComposerProps {
  value:           string;
  onChange:        (next: string) => void;
  onSubmit:        (text: string) => void;
  onStop?:         () => void;
  isStreaming?:    boolean;
  disabled?:       boolean;
  placeholder?:    string;
  /** Rotating prompts for idle landing — stops when the user types */
  placeholderPrompts?: string[];
  usePlaceholderTypewriter?: boolean;
  compact?:        boolean;
  filterTags?:     React.ReactNode;
  /** Source / model toggles — bottom-left row inside the container */
  sourceSelector?: React.ReactNode;
}

const MIN_HEIGHT_PX = 40;
const MAX_HEIGHT_PX = 144;

const AgentComposer = forwardRef<AgentComposerHandle, AgentComposerProps>(({
  value,
  onChange,
  onSubmit,
  onStop,
  isStreaming = false,
  disabled = false,
  placeholder = 'What kind of jobs are you looking for?',
  placeholderPrompts,
  usePlaceholderTypewriter = false,
  compact = false,
  filterTags,
  sourceSelector,
}, ref) => {
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const [focused, setFocused] = useState(false);

  const userHasTyped = value.length > 0;
  const showTypewriter = Boolean(
    usePlaceholderTypewriter
    && placeholderPrompts
    && placeholderPrompts.length > 0
    && !userHasTyped
    && !focused
    && !disabled
    && !isStreaming,
  );

  const typewriterText = useTypewriterPlaceholder(
    placeholderPrompts ?? [],
    showTypewriter,
  );

  useImperativeHandle(ref, () => ({
    setValue: (text: string) => { onChange(text); requestAnimationFrame(() => taRef.current?.focus()); },
    focus: () => taRef.current?.focus(),
  }), [onChange]);

  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(Math.max(el.scrollHeight, MIN_HEIGHT_PX), MAX_HEIGHT_PX);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT_PX ? 'auto' : 'hidden';
  }, [value]);

  const trimmed = value.trim();
  const canSubmit = !disabled && !isStreaming && trimmed.length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(trimmed);
  };

  const radius = '14px';
  const ariaPlaceholder = showTypewriter
    ? (typewriterText || placeholderPrompts?.[0] || placeholder)
    : placeholder;

  return (
    <div
      className={`relative w-full transition-shadow duration-200 ease-out ${sourceSelector ? 'z-30' : ''}`}
      style={{
        borderRadius: radius,
        padding: '1px',
        background: focused
          ? 'linear-gradient(135deg, rgba(108,71,255,0.45) 0%, rgba(99,102,241,0.35) 55%, rgba(124,58,237,0.30) 100%)'
          : 'var(--ds-border)',
        boxShadow: focused
          ? '0 0 0 3px var(--ds-brand-soft)'
          : '0 2px 12px rgba(15,23,42,0.04)',
      }}
    >
      <div
        className="overflow-visible bg-[var(--ds-bg-card)]"
        style={{ borderRadius: `calc(${radius} - 1px)` }}
      >
        {filterTags && (
          <div
            className="flex items-center gap-1 overflow-x-auto px-2.5 py-1"
            style={{ borderBottom: '1px solid var(--ds-divider)', background: 'var(--ds-bg-recessed)' }}
          >
            {filterTags}
          </div>
        )}

        <div className={`agent-composer-input-row relative flex items-end gap-2.5 ${compact ? 'p-2.5 agent-composer-input-row--compact' : 'px-4 py-3'}`}>
          {showTypewriter && (
            <div
              className="agent-composer-typewriter-overlay pointer-events-none absolute inset-x-0 top-0 bottom-0 flex items-start px-4 py-3"
              aria-hidden
            >
              <span className="agent-composer-typewriter min-w-0 flex-1 pr-12">
                {typewriterText}
                <StreamCaret className="opacity-70" style={{ color: 'inherit' }} />
              </span>
            </div>
          )}

          <textarea
            ref={taRef}
            rows={1}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
            }}
            placeholder={showTypewriter ? ' ' : placeholder}
            disabled={disabled}
            aria-label={ariaPlaceholder}
            className="agent-composer-input agent-textarea relative z-[1] min-w-0 flex-1 resize-none border-0 bg-transparent outline-none focus:ring-0"
            style={{
              minHeight: MIN_HEIGHT_PX,
              maxHeight: MAX_HEIGHT_PX,
            }}
          />

          {isStreaming ? (
            <button
              type="button"
              onClick={() => onStop?.()}
              className="agent-composer-action ds-btn ds-btn-secondary relative z-[1] shrink-0 min-h-[44px] min-w-[44px] !p-0"
              aria-label="Stop"
            >
              <Square style={{ width: 14, height: 14 }} />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="agent-composer-action ds-btn ds-btn-primary relative z-[1] shrink-0 min-h-[44px] min-w-[44px] !p-0"
              aria-label="Send message"
            >
              <ArrowUp style={{ width: 16, height: 16 }} />
            </button>
          )}
        </div>

        {sourceSelector && (
          <div className="agent-composer-toolbar relative z-30 flex shrink-0 flex-row flex-nowrap items-center gap-1 overflow-x-auto overscroll-x-contain rounded-b-[13px] border-t px-2 py-1.5 sm:flex-wrap sm:overflow-visible sm:justify-between sm:gap-x-2 sm:gap-y-1.5 sm:px-4 sm:py-2.5">
            {sourceSelector}
          </div>
        )}
      </div>
    </div>
  );
});

AgentComposer.displayName = 'AgentComposer';

export default AgentComposer;
